/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { PROPERTY_ID } from 'adv-protocol/src/GameCommon'
import BetCouponImg from '@assets/images/icon/matchup_icon/betting-coupon-500000.png'
import { GmoneyIco, HeartIco, MileageIco, PointIco, ScashIco } from '@shared/Icon/MoneyIcon'
import * as _ from 'lodash'
import { FEED_SUPPLIER, GlobalStoreKey } from '@v2/constants/common'
import { LS_SPORT_ID_ICON_MAP, SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import { Fragment } from 'react'
import { Button } from '@styles/button.style'
import {
  HYPER_TEXT_TRANSFER_PROTOCOL_PREFIX,
  HYPER_TEXT_TRANSFER_PROTOCOL_SECURE_PREFIX,
  URL_PREFIXES,
  URL_SEPARATORS,
  WORLD_WIDE_WEB_PREFIX,
} from '@constants/index'
import { FacebookEmbed, InstagramEmbed, TikTokEmbed, XEmbed, YouTubeEmbed } from 'react-social-media-embed'
import { isMobile } from 'react-device-detect'

import * as socialNetworkDetector from 'adv-lib/src/v2/utils/social-network-detector'

export function replaceStringToElement(text: string, from: string, to: JSX.Element, checkSpace = true) {
  let ret
  const arr = text.split(from)
  if (arr.length > 0) {
    ret = []
    let last
    arr.map((value, index) => {
      ret.push(arr[index])
      last = arr[index]
      if (index < arr.length - 1) {
        if (checkSpace === true) {
          if (index > 0) {
            if (last && last.endsWith(' ') === false) {
              last = `${last} `
            }
          }
        }
        ret.push(to)
      }
      return undefined
    })
  } else {
    ret = text
  }
  return ret
}

export function getIconByPropertyId(propertyId: PROPERTY_ID, className = 'bet-coupon-img') {
  switch (propertyId) {
    case PROPERTY_ID.MONEY:
      return <GmoneyIco/>
    case PROPERTY_ID.HEART:
      return <HeartIco/>
    case PROPERTY_ID.POINT:
      return <PointIco/>
    case PROPERTY_ID.CASH:
      return <ScashIco/>
    case PROPERTY_ID.MILEAGE:
      return <MileageIco/>
    case PROPERTY_ID.NEW_BET_TICKET_G:
      return <img src={BetCouponImg} alt="베팅권" className={className}/>
    default:
      return null
  }
}

/**
 * 중복을 제거하고 sportId 별로 오름차순 정렬한 스포츠 아이콘 엘리먼트를 반환한다.
 *
 * @param sportIdsParam
 * @param feedSupplier
 * @param limit
 */
export function getSportIconElementsAscendingOrderBySportIdAndRemoveDuplicated(
  sportIdsParam: number[], feedSupplier: FEED_SUPPLIER, limit = 3,
): JSX.Element[] {
  const ids = _.chain(sportIdsParam).sortBy().sortedUniq().value()
  const elements: JSX.Element[] = []
  _.forEach(ids, id => {
    if (_.size(elements) >= limit) {
      return
    }

    if (feedSupplier === FEED_SUPPLIER.BET_RADAR) {
      elements.push(SPORT_ID_ICON_MAP[id])
    } else if (feedSupplier === FEED_SUPPLIER.LSPORTS) {
      elements.push(LS_SPORT_ID_ICON_MAP[id])
    }
  })
  return elements
}

/**
 * 문자열에서 구분자들 중 가장 빠른 위치의 위치를 제공한다.
 *
 * @param {string} content
 * @param {string[]} separators
 * @param {number} startIndex
 */
export function getLeastIndexOfSeparators(content: string, separators: string[], startIndex = 0): number {
  let leastIndex = -1
  _.forEach(separators, separator => {
    const index = _.indexOf(content, separator, startIndex)
    if (index < 0) {
      return true
    }

    if (leastIndex < 0 || index < leastIndex) {
      leastIndex = index
    }
    return true
  })
  return leastIndex
}

/**
 * 문자열을 URL 구분자로(' ', '\n') 자른 목록을 생성한다. 접미사로 구분자를 포함한다.
 *
 * @param {string} content
 */
export function makeSplitSegmentsByUrlSeparatorsIncludeSeparatorSuffix(content: string): string [] {
  if (_.isNil(content)) {
    return ['']
  }
  if (_.isEmpty(_.trim(content))) {
    return [content]
  }

  let isUrlIncluded = _.includes(_.toLower(content), HYPER_TEXT_TRANSFER_PROTOCOL_SECURE_PREFIX)

  if (!isUrlIncluded) {
    isUrlIncluded = _.includes(_.toLower(content), HYPER_TEXT_TRANSFER_PROTOCOL_PREFIX)
  }

  if (!isUrlIncluded) {
    isUrlIncluded = _.includes(_.toLower(content), WORLD_WIDE_WEB_PREFIX)
  }

  // url 접두어가  있는 경우에만 작업을 진행한다.
  if (!isUrlIncluded) {
    return [content]
  }

  const segments = []
  const lastIndex = _.size(content) - 1
  let startIndex = 0
  let endIndex = 0

  while (startIndex <= lastIndex) {
    endIndex = getLeastIndexOfSeparators(content, URL_SEPARATORS, startIndex)
    if (endIndex < 0) {
      endIndex = lastIndex
    }

    segments.push(content.substring(startIndex, endIndex + 1))
    startIndex = endIndex + 1
  }
  return segments
}

const ChatLinkStyle = css`
    z-index: 0;
    width: auto;
    height: auto;
    padding: 6px 9px;
    background: #fff;
    border-radius: 4px;

    .sns-ico {
        width: auto;
        height: 30px;
        margin-right: 4px;
        &.x-ico {
          width: 25px;
          height: auto;
          margin: 3px 6px 2px 3px;
          border-radius: 5px;
        }
    }

    .txt {
        display: inline-block;
        transform: translateY(1px);
    }
`

/**
 * 문자열 중 문자열과 A태그를 구분하여 JSX Element를 생성한다.
 * 문자열 내 URL 패턴만 a 태그로 생성하고, 그 외에는 일반 span 으로 랜더링 한다.
 * 일반 사용자가 입력한 태그 및 스크립트 등이 동작하지 않도록 하기 위함.
 *
 * @param {string} content
 * @param {boolean} showEmbed
 * @param {function} callback
 */
export function makeATagAutoGenerationElements(
  globalStore: any, content: string, showEmbed = false, callback?: () => void,
): JSX.Element[] {
  let textSegment = ''

  // img
  const imageUrl = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL).value
  const facebookImg = `${imageUrl}icon/sns_icon/facebook.png`
  const instagramImg = `${imageUrl}icon/sns_icon/instagram.png`
  const tiktokImg = `${imageUrl}icon/sns_icon/tiktok.png`
  const xImg = `${imageUrl}icon/sns_icon/x.jpg`
  const youtubeImg = `${imageUrl}icon/sns_icon/youtube.png`

  const segments = makeSplitSegmentsByUrlSeparatorsIncludeSeparatorSuffix(content)
  return segments.map((segment, index) => {
    const loweredSegment = _.toLower(segment)
    let isUrl = false
    let isSocial = false
    let withHttpsPrefix = false
    let isX = false
    let isYoutube = false
    let isFacebook = false
    let isInstagram = false
    let isTiktok = false
    for (const prefix of URL_PREFIXES) {
      if (_.startsWith(loweredSegment, prefix)) {
        const detectResult = socialNetworkDetector.detect(loweredSegment)
        if (prefix === WORLD_WIDE_WEB_PREFIX) {
          withHttpsPrefix = true
        }
        if ((detectResult === 'x') && loweredSegment.includes('status')) {
          isX = true
          isSocial = true
        } else if (detectResult === 'youtube'
          && (loweredSegment.includes('watch') || loweredSegment.startsWith('https://youtu.be'))) {
          isYoutube = true
          isSocial = true
        } else if (detectResult === 'facebook' && loweredSegment.includes('posts')) {
          isFacebook = true
          isSocial = true
        } else if (detectResult === 'instagram' && loweredSegment.startsWith('https://www.instagram.com/p/')) {
          isInstagram = true
          isSocial = true
        } else if (detectResult === 'tiktok' && loweredSegment.includes('video')) {
          isTiktok = true
          isSocial = true
        }
        isUrl = true
        break
      }
    }

    const key = `${segment}_${index}`
    if (isUrl) {
      let mainElem
      // 1. URL + 텍스트
      // 2. 텍스트 + URL
      // 1번의 경우 URL 뒤에 공백이 포함되면서 소셜미디어 임베디드시 오류가 발생한다.
      // 그래서 소셜미디어의 경우 공백 제거 작업을 추가로 해준다.
      const urlValue = _.trim(segment)
      if (isX) {
        if (showEmbed) {
          mainElem = <XEmbed url={urlValue} width={isMobile ? '100%' : 149}/>
        } else {
          mainElem = (
            <Button
              css={ChatLinkStyle}
              onClick={(e) => {
                e.stopPropagation()
                _.isFunction(callback) && callback()
              }}>
              <img src={xImg} alt="X" className="sns-ico x-ico"/>
              <span className="txt">X 링크보기</span>
            </Button>
          )
        }
      } else if (isYoutube) {
        if (showEmbed) {
          mainElem = <YouTubeEmbed url={urlValue} width={isMobile ? '100%' : 149}/>
        } else {
          mainElem = (
            <Button
              css={ChatLinkStyle}
              onClick={(e) => {
                e.stopPropagation()
                _.isFunction(callback) && callback()
              }}>
              <img src={youtubeImg} alt="유튜브" className="sns-ico"/>
              <span className="txt">유튜브 링크보기</span>
            </Button>
          )
        }
      } else if (isTiktok) {
        if (showEmbed) {
          mainElem = <TikTokEmbed url={urlValue} width={isMobile ? '100%' : 149}/>
        } else {
          mainElem = (
            <Button
              css={ChatLinkStyle}
              onClick={(e) => {
                e.stopPropagation()
                _.isFunction(callback) && callback()
              }}>
              <img src={tiktokImg} alt="틱톡" className="sns-ico"/>
              <span className="txt">틱톡 링크보기</span>
            </Button>
          )
        }
      } else if (isInstagram) {
        if (showEmbed) {
          mainElem = <InstagramEmbed url={urlValue} width={isMobile ? '100%' : 149}/>
        } else {
          mainElem = (
            <Button
              css={ChatLinkStyle}
              onClick={(e) => {
                e.stopPropagation()
                _.isFunction(callback) && callback()
              }}>
              <img src={instagramImg} alt="인스타" className="sns-ico"/>
              <span className="txt">인스타 링크보기</span>
            </Button>
          )
        }
      } else if (isFacebook) {
        if (showEmbed) {
          mainElem = <FacebookEmbed url={urlValue} width={isMobile ? '100%' : 149}/>
        } else {
          mainElem = (
            <Button
              css={ChatLinkStyle}
              onClick={(e) => {
                e.stopPropagation()
                _.isFunction(callback) && callback()
              }}>
              <img src={facebookImg} alt="페이스북" className="sns-ico"/>
              <span className="txt">페이스북 링크보기</span>
            </Button>
          )
        }
      } else {
        // www. 로 시작하는 주소의 경우 브라우저에 종류에 따라 프로토콜을 명시적으로 추가해줘야 링크가 정상 동작 한다.
        const correctedUrl = withHttpsPrefix ? `${HYPER_TEXT_TRANSFER_PROTOCOL_SECURE_PREFIX}${segment}` : segment
        mainElem = <span><a href={correctedUrl} target="_blank" rel="noreferrer" className="link">{segment}</a></span>
      }

      if (!_.isEmpty(textSegment) && isSocial) {
        textSegment += '\n'
      }
      const urlElem = (
        <Fragment key={key}>
          {!_.isEmpty(textSegment) && <span>{textSegment}</span>}
          {mainElem}
        </Fragment>
      )
      textSegment = isSocial ? '\n' : ''
      return urlElem
    }

    textSegment += `${segment}`

    if (_.size(segments) - 1 <= index) {
      return (<span key={key}>{textSegment}</span>)
    }
    return null
  })
}

export function makeVolleyballScore(score) {
  const splitScore = score.split(' ')
  let periodScore = '-'
  const setScore = []
  if (splitScore.length > 0) {
    periodScore = splitScore[0]
  }

  if (splitScore.length > 1) {
    for (let i = 0; i < Number(splitScore[1]); i += 1) {
      setScore.push(<span key={i} className="volleyball-score--img"/>)
    }
  }

  return { periodScore, setScore }
}
